import React, { useState, useEffect } from 'react';
import {
  Paper,
  Title,
  TextInput,
  Textarea,
  Button,
  Stack,
  Text,
  LoadingOverlay,
  Group,
  Alert,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck, IconX, IconWand } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import CourseContentDisplay from './CourseContentDisplay';
import { CourseGeneratorFormValues, CourseState } from '../types';
import { API_CONFIG } from '../../../config/constants';
import { generateCoursePrompt } from '../utils/promptGenerator';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../auth/context/AuthContext';
import { courseService, StoredCourse } from '../services/courseService';
import { useCourse } from '../context/CourseContext';
import axios from 'axios';

const CourseGenerator = () => {
  const theme = useMantineTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { triggerRefresh } = useCourse();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ message: string; details?: string } | null>(null);
  const [currentCourse, setCurrentCourse] = useState<StoredCourse | null>(null);
  const [generatedContent, setGeneratedContent] = useState<any>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const form = useForm<CourseGeneratorFormValues>({
    initialValues: {
      topicName: '',
      additionalContext: '',
    },
    validate: {
      topicName: (value: string) => (!value ? 'Topic name is required' : null),
    },
  });

  const generateContent = async (values: CourseGeneratorFormValues) => {
    if (!user) {
      notifications.show({
        title: 'Authentication Required',
        message: 'Please sign in to generate course content',
        color: 'red',
        icon: <IconX />,
      });
      navigate('/login');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const prompt = generateCoursePrompt(values);
      
      const response = await axios.post(`${API_CONFIG.baseURL}/api/generate`, { prompt });
      const content = response.data;

      // Save the generated content to Firestore
      const courseId = await courseService.saveCourse(user.uid, values, content);
      const newCourse = {
        id: courseId,
        userId: user.uid,
        input: values,
        content,
        createdAt: new Date(),
        state: 'not_started' as CourseState
      };
      setCurrentCourse(newCourse);
      triggerRefresh(); // Trigger a refresh of the course history

      notifications.show({
        title: 'Success',
        message: 'Course content generated and saved successfully',
        color: 'green',
        icon: <IconCheck />,
      });

      navigate(`/course/${courseId}`, { state: { course: newCourse } });
    } catch (error) {
      console.error('Error generating content:', error);
      let errorMessage = 'An unexpected error occurred';
      let errorDetails = '';

      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || !navigator.onLine) {
          errorMessage = 'Network connection error';
          errorDetails = 'Please check your internet connection and try again.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Request timeout';
          errorDetails = 'The request took too long to complete. Please try again with a simpler topic or shorter requirements.';
        } else if (error.message.includes('Failed to generate content')) {
          errorMessage = 'Content generation failed';
          errorDetails = 'Our AI system had trouble generating the content. Please try again with different requirements or contact support if the issue persists.';
        } else {
          errorMessage = error.message;
          errorDetails = 'If this issue persists, please contact support.';
        }
      }

      setError({ message: errorMessage, details: errorDetails });

      notifications.show({
        title: 'Error',
        message: 'Failed to generate course content',
        color: 'red',
        icon: <IconX />,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setGeneratedContent(null);
    form.reset();
  };

  if (!user) {
    return null;
  }

  if (generatedContent) {
    return (
      <Stack spacing="xl">
        <Button 
          onClick={handleRetry}
          variant="light"
          color="indigo"
          fullWidth
        >
          Generate New Course
        </Button>
      </Stack>
    );
  }

  return (
    <Paper 
      p="xl" 
      radius="lg" 
      withBorder
      sx={{
        border: `2px solid ${theme.colors.gray[2]}`,
        background: 'linear-gradient(to bottom right, #ffffff, #f8f9fa)',
      }}
    >
      <LoadingOverlay 
        visible={loading} 
        overlayBlur={2}
        loaderProps={{ size: 'xl', variant: 'dots' }}
      />
      
      {error && (
        <Alert 
          icon={<IconAlertCircle size={24} />} 
          title={error.message}
          color="red" 
          mb="xl"
          radius="md"
          withCloseButton
          onClose={() => setError(null)}
          styles={{
            title: { fontSize: '1.1rem' },
          }}
        >
          {error.details}
        </Alert>
      )}

      <Stack spacing="xl">
        <Group spacing="xs" align="center">
          <IconWand size={32} color={theme.colors.indigo[6]} />
          <Title order={2} sx={{ color: theme.colors.indigo[9] }}>
            Ask the Learning Genie!
          </Title>
        </Group>

        <form onSubmit={form.onSubmit(generateContent)}>
          <Stack spacing="md">
            <TextInput
              required
              label="What would you like to learn?"
              placeholder="Enter a topic or skill..."
              {...form.getInputProps('topicName')}
            />

            <Textarea
              label="Additional Context (Optional)"
              placeholder="Add any specific requirements or focus areas..."
              minRows={3}
              {...form.getInputProps('additionalContext')}
            />

            <Button
              type="submit"
              size="lg"
              leftIcon={<IconWand size={20} />}
              loading={loading}
              sx={(theme) => ({
                background: `linear-gradient(135deg, ${theme.colors.indigo[6]}, ${theme.colors.indigo[8]})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.colors.indigo[7]}, ${theme.colors.indigo[9]})`,
                },
              })}
            >
              Generate Course Content
            </Button>
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
};

export default CourseGenerator; 